'use client';

import { PropsWithChildren, createContext, useContext } from 'react';
import { useSelectedFilters } from './useSelectedFilters';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { inline } from '@/uiPrimitives/utility/display.css';
import { useIsNavigating } from '@/globals/routing/DOPRouter';
import { WebAPIOptionFilter } from '@/globals/webApiTypes/elements/WebAPIEvent';

type FilterOptionContextValue = {
	groupKey: string;
	optionKey: string;
};

const FilterOptionContext = createContext<FilterOptionContextValue | undefined>(
	undefined,
);

export const FilterOptionProvider = ({
	children,
	...value
}: PropsWithChildren<FilterOptionContextValue>) => {
	return (
		<FilterOptionContext.Provider value={value}>
			{children}
		</FilterOptionContext.Provider>
	);
};

export const useFilterOptionContext = () => {
	const value = useContext(FilterOptionContext);

	if (value == null) {
		throw new Error(
			'useFilterOptionContext must be used within a FilterOptionProvider',
		);
	}

	return value;
};

export const FilterOptionPreviewCount = ({
	filterTypes,
}: {
	filterTypes: Array<WebAPIOptionFilter> | undefined;
}) => {
	const { groupKey, optionKey } = useFilterOptionContext();

	const [selectedFilters] = useSelectedFilters();

	const loading = useIsNavigating();

	const checked = selectedFilters?.get(groupKey)?.has(optionKey) ?? false;

	if (checked) return null;

	const filterType = filterTypes?.find(
		(filterType) => filterType.key === groupKey,
	);
	const option = filterType?.optionList.find(
		(option) => option.id === optionKey,
	);
	const resultCountPreview = option?.resultCountPreview;

	if (resultCountPreview == null) return null;

	return (
		<span className={classes(box({ opacity: loading ? '0' : '1' }), inline)}>
			{'('}
			{resultCountPreview}
			{')'}
		</span>
	);
};
